import PropTypes from 'prop-types';

import {MARKET_DATA} from 'context/MarketInfo/marketConfig';

export const userInfoPropType = PropTypes.exact({
  loading: PropTypes.bool.isRequired,
  error: PropTypes.bool.isRequired,
  data: PropTypes.exact({
    username: PropTypes.string.isRequired,
    displayName: PropTypes.string.isRequired,
    email: PropTypes.string,
    avatar: PropTypes.string,
    product: PropTypes.oneOf(['free', 'open', 'premium']).isRequired,
    followerCount: PropTypes.number.isRequired,
    // The following params are only available in the scope of close-account steps
    // (due to the extra fetch to HAPI /overview)
    followingCount: PropTypes.number,
    playlistCount: PropTypes.number,
    createdFromFb: PropTypes.bool,
    hasStudentDiscount: PropTypes.bool,
    phoneNumber: PropTypes.string,
  }),
}).isRequired;

export const marketInfoPropType = PropTypes.exact({
  market: PropTypes.string.isRequired,
  country: PropTypes.string.isRequired,
  locale: PropTypes.string.isRequired,
  sfMarket: PropTypes.string,
  phonePrefix: PropTypes.string,
}).isRequired;

export const configPropType = PropTypes.exact({
  supportSiteUrl: PropTypes.func.isRequired,
  spotifyComUrl: PropTypes.func.isRequired,
  spAccountsUrl: PropTypes.func.isRequired,
  spEmbedUrl: PropTypes.func.isRequired,
  caseCreateUrl: PropTypes.string.isRequired,
  contactformAnonymous: PropTypes.func.isRequired,
  plainUI: PropTypes.bool.isRequired,
}).isRequired;

const marketFiltersPropTypes = {
  markets: PropTypes.arrayOf(PropTypes.string),
  excluded_markets: PropTypes.arrayOf(PropTypes.string),
};

const contactFormFieldBasePropType = {
  ...marketFiltersPropTypes,
  name: PropTypes.string,
  type: PropTypes.oneOf(['text', 'textarea', 'select', 'phone', 'email', 'radio', 'banner']),
  placeholder: PropTypes.bool,
  placeholderOverride: PropTypes.string,
  help_text: PropTypes.bool,
  order: PropTypes.number,
  maxlength: PropTypes.number,
  required: PropTypes.bool,
  required_with: PropTypes.string,
  options: PropTypes.arrayOf(PropTypes.string),
  visible_logged_in: PropTypes.bool,
  label: PropTypes.string,
  labelOverride: PropTypes.string,
  title: PropTypes.string,
  content: PropTypes.string,
  depends_on: PropTypes.exact({
    // For type "select" only - there is custom logic to clear out unused formData from select-boxes in place
    field: PropTypes.string.isRequired,
    for_values: PropTypes.arrayOf(PropTypes.string).isRequired,
  }),
};

// Override match logic allows only a certain set of keys - either markets or the few custom cases
const ALLOWED_MATCH_KEYS = Object.keys(MARKET_DATA)
  .map(market => `market:${market}`)
  .concat(['chatbot', 'chat', 'case', 'logged_in', 'logged_out']);

const overrideMatchPropType = (array, key, componentName) => {
  if (!ALLOWED_MATCH_KEYS.includes(array[key])) {
    return new Error(`Invalid value (${array[key]}) for override match, supplied to ${componentName}`);
  }
  return true;
};

export const contactFormFieldPropType = PropTypes.exact({
  ...contactFormFieldBasePropType,
  overrides: PropTypes.arrayOf(
    PropTypes.exact({
      match_all: PropTypes.arrayOf(overrideMatchPropType),
      match_any: PropTypes.arrayOf(overrideMatchPropType),
      data: PropTypes.exact(contactFormFieldBasePropType).isRequired,
    })
  ),
}).isRequired;

export const reportContentFormFieldPropType = PropTypes.exact({
  ...marketFiltersPropTypes,
  name: PropTypes.string.isRequired,
  type: PropTypes.oneOf(['text', 'textarea', 'select', 'email', 'radio', 'checkbox']).isRequired,
  hasDefaultPlaceholder: PropTypes.bool,
  help_text: PropTypes.bool,
  order: PropTypes.number,
  maxlength: PropTypes.number,
  required: PropTypes.bool,
  options: PropTypes.arrayOf(PropTypes.string),
  visible_logged_in: PropTypes.bool,
  label: PropTypes.string,
  depends_on: PropTypes.exact({
    // For type "select" only
    field: PropTypes.string.isRequired,
    for_values: PropTypes.arrayOf(PropTypes.string).isRequired,
  }),
}).isRequired;

export const notificationPropType = PropTypes.exact({
  body: PropTypes.string.isRequired,
  type: PropTypes.oneOf(['issue', 'info', 'resolved']).isRequired,
  lastUpdated: PropTypes.string.isRequired,
}).isRequired;

export const reportContentFormComponentPropType = PropTypes.exact({
  t: PropTypes.func.isRequired,
  traw: PropTypes.func.isRequired,
  formData: PropTypes.object.isRequired,
  goToNextStep: PropTypes.func.isRequired,
  handleFieldChange: PropTypes.func.isRequired,
  market: PropTypes.string.isRequired,
  renderErrorMessage: PropTypes.func.isRequired,
  forceValidation: PropTypes.bool.isRequired,
}).isRequired;
